import { Modal, Text, Flex, Button, Paper, List, ThemeIcon } from "@mantine/core";
import { IconAlertOctagon, IconAlertCircle, IconEye } from "@tabler/icons-react";

interface RejectionDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  adminComments?: string | null;
}

export default function RejectionDetailsModal({ opened, onClose, adminComments }: RejectionDetailsModalProps) {
  const handleReviewAndEdit = () => {
    onClose();
    window.location.href = "/profile/edit";
  };

  const parseReasons = (comments: string | null | undefined): string[] => {
    if (!comments || !comments.trim()) {
      return [];
    }

    // Split by newlines and clean up
    const lines = comments
      .split(/\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      // Remove common list prefixes (1., 2., -, *, •)
      .map((line) => line.replace(/^[\d]+\.\s*|^[-*•]\s*/, "").trim())
      .filter((line) => line.length > 0);

    // If no lines after parsing, return the original comment as single reason
    if (lines.length === 0) {
      return [comments.trim()];
    }

    return lines;
  };

  const reasons = parseReasons(adminComments);
  const hasReasons = reasons.length > 0;

  return (
    <Modal opened={opened} onClose={onClose} withCloseButton={true} centered size="lg">
      <Flex direction="column" align="center" py="md">
        {/* Icon */}
        <ThemeIcon
          size={64}
          radius={64}
          variant="light"
          color="red"
          mb="md"
          styles={{
            root: {
              backgroundColor: "#D84B4B1A",
              color: "#D84B4B",
            },
          }}
        >
          <IconAlertOctagon size={32} />
        </ThemeIcon>

        {/* Title */}
        <Text fw={600} fz="xl" c="slate.8" ta="center" mb="lg">
          Verification Rejected
        </Text>

        {/* Rejection Reasons Card */}
        {hasReasons ? (
          <Paper
            p="md"
            radius="lg"
            mb="xl"
            w="100%"
            style={{
              backgroundColor: "#D84B4B1A",
              border: "1px solid #D84B4B33",
            }}
          >
            <Flex gap="sm" align="flex-start">
              <ThemeIcon size={20} radius="xl" color="red" variant="transparent">
                <IconAlertCircle size={18} color="#D84B4B" />
              </ThemeIcon>
              <Flex direction="column" style={{ flex: 1 }}>
                <Text fz="sm" fw={600} c="#D84B4B" mb="xs">
                  Reason for Rejection
                </Text>
                {reasons.length === 1 ? (
                  // Single reason - display as text
                  <Text fz="sm" c="#D84B4B">
                    {reasons[0]}
                  </Text>
                ) : (
                  // Multiple reasons - display as list
                  <List
                    size="sm"
                    spacing={4}
                    styles={{
                      item: {
                        color: "#D84B4B",
                        fontSize: "13px",
                      },
                    }}
                  >
                    {reasons.map((reason, index) => (
                      <List.Item key={index}>{reason}</List.Item>
                    ))}
                  </List>
                )}
              </Flex>
            </Flex>
          </Paper>
        ) : (
          // No reasons provided - show generic message
          <Paper
            p="md"
            radius="lg"
            mb="xl"
            w="100%"
            style={{
              backgroundColor: "#D84B4B1A",
              border: "1px solid #D84B4B33",
            }}
          >
            <Flex gap="sm" align="flex-start">
              <ThemeIcon size={20} radius="xl" color="red" variant="transparent">
                <IconAlertCircle size={18} color="#D84B4B" />
              </ThemeIcon>
              <Flex direction="column">
                <Text fz="sm" fw={600} c="#D84B4B" mb="xs">
                  Verification Requirements Not Met
                </Text>
                <Text fz="sm" c="#D84B4B">
                  Your profile verification was rejected. Please review your profile and ensure all information is
                  accurate and complete.
                </Text>
              </Flex>
            </Flex>
          </Paper>
        )}

        {/* Button */}
        <Button fullWidth onClick={handleReviewAndEdit} leftSection={<IconEye size={20} />}>
          Review & Edit Profile
        </Button>
      </Flex>
    </Modal>
  );
}
